/**
 * Game start against a backend that may not be reachable yet: connects (transport + protocol handshake) and, while
 * the server is unavailable, retries with exponential backoff (1 s doubling up to 30 s) until it answers. There is no
 * fallback to the offline backend: offline play is always an explicit choice (?backend=offline). The host shows the
 * reported state on its loading screen and may call `retryNow()` (click / key press) to skip the wait.
 */
import { BackendError } from '../../shared/protocol/errors';
import { type BackendConnectionConfig, describeBackend } from '../config';
import { type GameServices, type GameServicesConfig, createGameServices } from '../game-services';
import { TransportError } from './transport';

export type StartupState =
  | { phase: 'connecting'; address: string; attempt: number }
  /** The server is unavailable; the next attempt starts at `retryAt` (the options' `now()` clock, ms). */
  | { phase: 'waiting'; address: string; attempt: number; retryAt: number; code: string; message: string }
  /** A failure that waiting cannot fix (e.g. VERSION_MISMATCH); `retryNow()` still tries once more. */
  | { phase: 'failed'; address: string; code: string; message: string };

export interface StartupOptions {
  onState?: (state: StartupState) => void;
  /** First retry delay (default 1000 ms); doubles per attempt. */
  minDelayMs?: number;
  /** Longest retry delay (default 30 000 ms). */
  maxDelayMs?: number;
  now?: () => number;
  /** Creates the services for one attempt (default createGameServices). */
  connect?: (config: GameServicesConfig) => Promise<GameServices>;
}

export interface StartupConnection {
  /** Resolves with connected services; rejects only after `cancel()`. */
  readonly services: Promise<GameServices>;
  /** Starts the next attempt right away (while waiting or after a failure). */
  retryNow(): void;
  cancel(): void;
}

/** Codes of a server that is down, restarting or overloaded: worth waiting for. */
const WAITABLE = new Set(['UNAVAILABLE', 'RATE_LIMITED', 'INTERNAL']);

/** Error code of a failed start ('UNAVAILABLE' for transport failures) and whether waiting can fix it. */
export function classifyStartupError(err: unknown): { code: string; message: string; retry: boolean } {
  const message = err instanceof Error ? err.message : String(err);
  if (err instanceof BackendError) return { code: err.code, message, retry: WAITABLE.has(err.code) };
  if (err instanceof TransportError) return { code: 'UNAVAILABLE', message, retry: err.retryable };
  return { code: 'INTERNAL', message, retry: false };
}

/** Retry delay before attempt `attempt + 1` (attempt counts from 1): 1 s, 2 s, 4 s, ... capped. */
export function startupBackoffMs(attempt: number, minDelayMs = 1000, maxDelayMs = 30_000): number {
  return Math.min(maxDelayMs, minDelayMs * 2 ** Math.max(0, attempt - 1));
}

export function connectWithRetry(config: GameServicesConfig & BackendConnectionConfig, opts: StartupOptions = {}): StartupConnection {
  const address = describeBackend(config);
  const now = opts.now ?? (() => Date.now());
  const connect = opts.connect ?? createGameServices;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let running = false;
  let cancelled = false;
  let connected = false;
  let resolve!: (s: GameServices) => void;
  let reject!: (e: Error) => void;
  const services = new Promise<GameServices>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  const report = (s: StartupState) => {
    try {
      opts.onState?.(s);
    } catch {
      /* a broken status display must not stop the start */
    }
  };

  const run = async () => {
    if (running || cancelled || connected) return;
    running = true;
    attempt++;
    report({ phase: 'connecting', address, attempt });
    try {
      const s = await connect(config);
      if (cancelled) {
        await s.dispose().catch(() => undefined);
        return;
      }
      connected = true;
      resolve(s);
    } catch (err) {
      if (cancelled) return;
      const e = classifyStartupError(err);
      if (e.retry) {
        const delay = startupBackoffMs(attempt, opts.minDelayMs, opts.maxDelayMs);
        report({ phase: 'waiting', address, attempt, retryAt: now() + delay, code: e.code, message: e.message });
        timer = setTimeout(() => {
          timer = null;
          void run();
        }, delay);
      } else {
        report({ phase: 'failed', address, code: e.code, message: e.message });
      }
    } finally {
      running = false;
    }
  };

  void run();
  return {
    services,
    retryNow() {
      if (cancelled || running || connected) return;
      if (timer) clearTimeout(timer);
      timer = null;
      void run();
    },
    cancel() {
      if (cancelled || connected) return;
      cancelled = true;
      if (timer) clearTimeout(timer);
      timer = null;
      reject(new Error('Backend connection cancelled'));
    },
  };
}
